import { App } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getApiError,
    getSchedulerDayPointScheduleThunk,
    getSchedulerPointScheduleDetailsThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Point, WorkStatus } from "tm-shared/types";

import { sortPoints } from "@/domains/point";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { getPointInfoCollectionUnplannedThunk } from "@/store/slices/work";
import { isManagerRole } from "@/utils/business/isManagerRole";

export const useUnplannedPoints = () => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();

    // User and route state
    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);
    const route = useAppSelector((s) => s.user.route);
    const activeSchedule = useAppSelector(
        (state) => state.manager.activeSchedule,
    );

    // Work and UI state
    const pointIds = useAppSelector((state) => state.work.pointIds);
    const pointExtraIds = useAppSelector((state) => state.work.pointExtraIds);
    const workStatus = useAppSelector((state) => state.work.workStatus);
    const currentRouteList = useAppSelector(
        (state) => state.work.currentRouteList,
    );
    const unplannedPoints = useAppSelector(
        (state) => state.work.unplannedPoints,
    );
    const pointsSortOption = useAppSelector(
        (state) => state.ui.workPlan.pointsSortOption,
    );

    // Local UI state
    const [selectedDay, setSelectedDay] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPoints, setSelectedPoints] = useState<Point[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [loadingPoints, setLoadingPoints] = useState(false);

    // Local refs and derived flags
    const fetchedPointIdsRef = useRef<Set<string>>(new Set());
    const localLoading = loadingSchedule || loadingPoints;

    // Available unplanned points for current filters
    const uniquePoints = useMemo(() => {
        const pointIdsSet = new Set(pointIds);

        return Object.values(unplannedPoints)
            .map((point) => ({
                ...point,
                days: currentRouteList.find(
                    ({ pointId }) => pointId === point.id,
                )?.days,
            }))
            .filter((point) => !pointIdsSet.has(point.id))
            .filter(
                (point) =>
                    selectedDay === 0 || point.days?.includes(selectedDay),
            );
    }, [unplannedPoints, currentRouteList, pointIds, selectedDay]);

    // Search and sort
    const filteredPoints = useMemo(() => {
        if (!searchTerm) {
            return sortPoints(uniquePoints, pointsSortOption?.value || "route");
        }

        const lower = searchTerm.toLowerCase();

        return sortPoints(
            uniquePoints.filter(
                ({ name, market: { contragent } }) =>
                    name.toLowerCase().includes(lower) ||
                    contragent.name.toLowerCase().includes(lower),
            ),
            pointsSortOption?.value || "route",
        );
    }, [uniquePoints, searchTerm, pointsSortOption]);

    // Selection handlers
    const togglePointSelection = useCallback((point: Point) => {
        setSelectedPoints((prev) => {
            const isSelected = prev.some((m) => m.id === point.id);

            return isSelected
                ? prev.filter((m) => m.id !== point.id)
                : [...prev, point];
        });
    }, []);

    // Load route schedule data
    useEffect(() => {
        if (!userId || workStatus !== WorkStatus.StartWork) return;

        const fetchSchedule = async () => {
            setLoadingSchedule(true);

            try {
                const today = dayjs().format(backendDateFormat.date);

                const routeId = isManagerRole(role)
                    ? activeSchedule?.routeId
                    : route?.uuid;

                if (!routeId) throw new Error("routeId is required");

                await Promise.all([
                    dispatch(
                        getSchedulerDayPointScheduleThunk({
                            routeId,
                            date: today,
                        }),
                    ).unwrap(),
                    dispatch(
                        getSchedulerPointScheduleDetailsThunk({
                            routeId,
                        }),
                    ).unwrap(),
                ]);
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка при завантаженні даних",
                    description: displayMessage,
                });

                console.error("Failed to fetch schedule:", error);
            } finally {
                setLoadingSchedule(false);
            }
        };

        fetchSchedule();
    }, [
        userId,
        notification,
        workStatus,
        dispatch,
        role,
        activeSchedule?.routeId,
        route?.uuid,
    ]);

    // Load details for points that are not in the main route list yet
    useEffect(() => {
        if (!currentRouteList?.length) return;

        const pointIdsSet = new Set(pointIds);
        const newPointIds = currentRouteList
            .map(({ pointId }) => pointId)

            .filter(
                (id) =>
                    id &&
                    !pointIdsSet.has(id) &&
                    !fetchedPointIdsRef.current.has(id),
            );

        if (!newPointIds.length) return;

        const fetchPointsInfo = async () => {
            setLoadingPoints(true);

            newPointIds.forEach((id) => fetchedPointIdsRef.current.add(id));

            try {
                await dispatch(
                    getPointInfoCollectionUnplannedThunk({
                        ids: newPointIds,
                    }),
                ).unwrap();
            } catch (error) {
                console.error("Failed to fetch points info:", error);

                newPointIds.forEach((id) =>
                    fetchedPointIdsRef.current.delete(id),
                );
            } finally {
                setLoadingPoints(false);
            }
        };

        fetchPointsInfo();
    }, [currentRouteList, pointIds, dispatch]);

    return {
        filteredPoints,
        selectedDay,
        setSelectedDay,
        searchTerm,
        setSearchTerm,
        selectedPoints,
        togglePointSelection,
        openModal,
        setOpenModal,
        pointExtraIds,
        userId,
        loading: localLoading,
    };
};
